import { useEffect, useState } from "react";
import { Loader2, Plus, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";
import CompanyHistory from "./CompanyHistory";

const inputCls = "h-8 w-full rounded-md border border-slate-200 px-2 text-xs";
const FALLBACK_CLASSES = [
  { key: "customer", label: "Customer" },
  { key: "vendor", label: "Vendor" },
  { key: "subcontractor", label: "Subcontractor" },
];
// Mirrors the backend default: one class per company, Customer > Subcontractor > Vendor.
const CLASS_FOR_TYPE = { gc: "customer", owner: "customer", architect: "customer",
                         manufacturer: "vendor", supplier: "vendor",
                         subcontractor: "subcontractor" };
const defaultClassFor = (types = []) => {
  const mapped = new Set(types.map((t) => CLASS_FOR_TYPE[t]).filter(Boolean));
  return ["customer", "subcontractor", "vendor"].find((c) => mapped.has(c)) || "customer";
};
const formatNoteDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric",
                                    hour: "numeric", minute: "2-digit" });
};
const ROLES = [
  ["procurement", "Procurement / ordering"],
  ["project_manager", "Project manager"],
  ["estimating", "Estimating"],
  ["accounting", "Accounting / AP"],
  ["other", "Other"],
];

/** Edit one company and the people at it, including who purchase orders go to. */
export default function CompanyDrawer({ company, types, classes = [], contacts, onClose, onSaved, footer = null }) {
  const isNew = !company.id;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const [form, setForm] = useState({
    name: company.name || "",
    types: company.types || (company.company_type ? [company.company_type] : []),
    address: company.address || "",
    city: company.city || "",
    state: company.state || "",
    zip_code: company.zip_code || "",
    shipping_same_as_corporate: company.shipping_same_as_corporate !== false,
    shipping_address: company.shipping_address || "",
    shipping_city: company.shipping_city || "",
    shipping_state: company.shipping_state || "",
    shipping_zip: company.shipping_zip || "",
    phone: company.phone || "",
    website: company.website || "",
    account_number: company.account_number || "",
    foundation_id: company.foundation_id || "",
    procurement_contact_id: company.procurement_contact_id || "",
    procurement_email: company.procurement_email || "",
    company_class: company.company_class || "",
  });
  const [noteEntries, setNoteEntries] = useState(company.note_entries || []);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [people, setPeople] = useState(contacts);
  const [adding, setAdding] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: "", last_name: "", title: "",
    email: "", phone: "", role: "other" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Class defaults from Type — Customer wins, then Subcontractor, then Vendor — but stays put
  // once somebody has set it by hand (Home Depot is a Supplier we buy from, Mortenson a Customer).
  const toggleType = (key) => setForm((p) => {
    const types = p.types.includes(key) ? p.types.filter((t) => t !== key) : [...p.types, key];
    const derived = defaultClassFor(types);
    const wasDerived = !p.company_class || p.company_class === defaultClassFor(p.types);
    return { ...p, types, company_class: wasDerived ? derived : p.company_class };
  });
  const orders = form.types.some((t) => ["manufacturer", "supplier", "subcontractor"].includes(t));

  const save = async () => {
    if (!form.name.trim()) return toast.error("A company name is required");
    if (!form.types.length) return toast.error("Pick at least one type — what are they to us?");
    setBusy(true);
    let saved = null;
    try {
      if (isNew) saved = await estApi.createCompany({ ...form, name: form.name.trim() });
      else saved = await estApi.updateCompany(company.id, form);
      toast.success(isNew ? `${form.name.trim()} added` : "Company saved");
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const entry = await estApi.addCompanyNote(company.id, text);
      setNoteEntries((n) => [...n, entry]);
      setNewNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the note");
    } finally {
      setSavingNote(false);
    }
  };

  const addContact = async () => {
    if (!newContact.first_name && !newContact.last_name && !newContact.email) {
      return toast.error("A name or an email is required");
    }
    try {
      const c = await estApi.createContact({ ...newContact, company_id: company.id });
      setPeople((p) => [...p, c]);
      setNewContact({ first_name: "", last_name: "", title: "", email: "", phone: "", role: "other" });
      setAdding(false);
      toast.success("Contact added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add contact");
    }
  };

  const setPrimary = async (contact) => {
    await Promise.all(people.filter((p) => p.is_primary && p.id !== contact.id)
      .map((p) => estApi.updateContact(p.id, { is_primary: false })));
    await estApi.updateContact(contact.id, { is_primary: true });
    setPeople((p) => p.map((x) => ({ ...x, is_primary: x.id === contact.id })));
    toast.success(`${contact.display_name} is the primary contact`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="company-drawer">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-800">
            {isNew ? "New company" : form.name}
          </h2>
          <button onClick={onClose} data-testid="company-drawer-close"
                  className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <Field label="Company name">
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
                   className={inputCls} data-testid="company-name" autoFocus={isNew} />
          </Field>

          <Field label="Type">
            <div className="flex flex-wrap gap-1.5">
              {types.map((t) => (
                <button key={t.key} onClick={() => toggleType(t.key)}
                        data-testid={`company-type-${t.key}`}
                        className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                          form.types.includes(t.key)
                            ? "border-[#007BFF] bg-blue-50 text-[#007BFF] font-medium"
                            : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Class" hint="Defaults from Type. Change it only for exceptions.">
            <select value={form.company_class || defaultClassFor(form.types)}
                    onChange={(e) => set("company_class", e.target.value)}
                    className={inputCls} data-testid="company-class">
              {(classes.length ? classes : FALLBACK_CLASSES).map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                     className={inputCls} data-testid="company-phone" />
            </Field>
            <Field label="Website">
              <input value={form.website} onChange={(e) => set("website", e.target.value)}
                     className={inputCls} data-testid="company-website" />
            </Field>
          </div>
          <Field label="Corporate address">
            <input value={form.address} onChange={(e) => set("address", e.target.value)}
                   className={inputCls} data-testid="company-address" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City">
              <input value={form.city} onChange={(e) => set("city", e.target.value)}
                     className={inputCls} data-testid="company-city" />
            </Field>
            <Field label="State">
              <input value={form.state} onChange={(e) => set("state", e.target.value)}
                     className={inputCls} data-testid="company-state" />
            </Field>
            <Field label="ZIP">
              <input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)}
                     className={inputCls} data-testid="company-zip" />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={form.shipping_same_as_corporate}
                   onChange={(e) => set("shipping_same_as_corporate", e.target.checked)}
                   className="h-3.5 w-3.5 rounded border-slate-300"
                   data-testid="company-shipping-same" />
            Shipping address same as corporate
          </label>

          {!form.shipping_same_as_corporate && (
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <Field label="Shipping address">
                <input value={form.shipping_address}
                       onChange={(e) => set("shipping_address", e.target.value)}
                       className={inputCls} data-testid="company-shipping-address" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="City">
                  <input value={form.shipping_city}
                         onChange={(e) => set("shipping_city", e.target.value)}
                         className={inputCls} data-testid="company-shipping-city" />
                </Field>
                <Field label="State">
                  <input value={form.shipping_state}
                         onChange={(e) => set("shipping_state", e.target.value)}
                         className={inputCls} data-testid="company-shipping-state" />
                </Field>
                <Field label="ZIP">
                  <input value={form.shipping_zip}
                         onChange={(e) => set("shipping_zip", e.target.value)}
                         className={inputCls} data-testid="company-shipping-zip" />
                </Field>
              </div>
            </div>
          )}

          {orders && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Ordering
              </p>
              <p className="mb-2 text-[10px] text-slate-400">
                Purchase orders and quote requests go to this person by default.
              </p>
              <Field label="Primary procurement contact">
                <select value={form.procurement_contact_id || ""}
                        onChange={(e) => set("procurement_contact_id", e.target.value)}
                        className={inputCls} data-testid="company-procurement-contact">
                  <option value="">— none chosen —</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name || `${p.first_name} ${p.last_name}`.trim()}
                      {p.email ? ` · ${p.email}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="mt-2 grid grid-cols-3 gap-3">
                <Field label="Order email">
                  <input value={form.procurement_email}
                         onChange={(e) => set("procurement_email", e.target.value)}
                         placeholder="orders@vendor.com"
                         className={inputCls} data-testid="company-procurement-email" />
                </Field>
                <Field label="Account #">
                  <input value={form.account_number}
                         onChange={(e) => set("account_number", e.target.value)}
                         className={inputCls} data-testid="company-account-number" />
                </Field>
                <Field label="Foundation ID">
                  <input value={form.foundation_id}
                         onChange={(e) => set("foundation_id", e.target.value)}
                         className={inputCls} data-testid="company-foundation-id" />
                </Field>
              </div>
            </div>
          )}

          <Field label="Notes" hint="Each note is kept with who wrote it and when.">
            {noteEntries.length > 0 && (
              <ul className="mb-2 space-y-2" data-testid="company-note-log">
                {[...noteEntries].reverse().map((n) => (
                  <li key={n.id} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                    <p className="whitespace-pre-wrap text-xs text-slate-700">{n.text}</p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {n.author} · {formatNoteDate(n.at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {isNew ? (
              <p className="text-[11px] text-slate-400">Save the company first, then add notes.</p>
            ) : (
              <div className="flex items-start gap-2">
                <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={2}
                          placeholder="Add a note…"
                          className="w-full rounded-md border border-slate-200 p-2 text-xs"
                          data-testid="company-new-note" />
                <Button size="sm" variant="outline" className="h-8 shrink-0 text-[11px]"
                        disabled={!newNote.trim() || savingNote}
                        onClick={addNote} data-testid="company-add-note">
                  {savingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                </Button>
              </div>
            )}
          </Field>

          {!isNew && (
            <div className="border-t border-slate-100 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                  People ({people.length})
                </span>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={() => setAdding((a) => !a)} data-testid="company-add-contact">
                  <Plus className="mr-1 h-3 w-3" /> Contact
                </Button>
              </div>

              {adding && (
                <div className="mb-3 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="First name" value={newContact.first_name}
                           onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                           className={inputCls} data-testid="new-contact-first" />
                    <input placeholder="Last name" value={newContact.last_name}
                           onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                           className={inputCls} data-testid="new-contact-last" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Email" value={newContact.email}
                           onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                           className={inputCls} data-testid="new-contact-email" />
                    <input placeholder="Phone" value={newContact.phone}
                           onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                           className={inputCls} data-testid="new-contact-phone" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Title" value={newContact.title}
                           onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                           className={inputCls} data-testid="new-contact-title" />
                    <select value={newContact.role} data-testid="new-contact-role"
                            onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                            className={inputCls}>
                      {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <Button size="sm" className="h-7 w-full text-[11px]" onClick={addContact}
                          data-testid="new-contact-save">Add contact</Button>
                </div>
              )}

              <div className="space-y-1">
                {people.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 rounded border border-slate-100 px-2 py-1.5"
                       data-testid={`company-contact-${p.id}`}>
                    <button onClick={() => setPrimary(p)} title="Make primary contact"
                            data-testid={`company-contact-primary-${p.id}`}>
                      <Star className={`h-3.5 w-3.5 ${p.is_primary
                        ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
                    </button>
                    <span className="text-xs font-medium text-slate-700">
                      {p.display_name || `${p.first_name} ${p.last_name}`.trim()}
                    </span>
                    <span className="text-[10px] text-slate-400">{p.title}</span>
                    <span className="ml-auto text-[10px] text-slate-500">
                      {[p.email, p.phone || p.mobile].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                ))}
                {!people.length && (
                  <p className="py-2 text-[11px] text-slate-400">
                    Nobody here yet — add the person you actually deal with.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        {company?.id && <CompanyHistory companyId={company.id} />}

        {/* pr-24 keeps the buttons clear of the floating Mira button */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 py-3 pl-5 pr-24">
          {footer && <div className="mr-auto">{footer}</div>}
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="company-save">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {isNew ? "Add company" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      {hint && <span className="ml-2 text-[10px] text-slate-400">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
